/* ==========================================================================
   custom.js — mobile-only bottom tab nav behavior
   - Hide on scroll down / show on scroll up (scroll-delta threshold).
   - Cart count badge reads from Prestige's cart event bus (same pattern as
     theme.js CartNotification / cart-count: listens to `cart:change` for
     incremental updates and `cart:refresh` to refetch from /cart.js).
   - No dependencies on theme.js internals; uses only public events.
   ==========================================================================*/

(function () {
  'use strict';

  var MOBILE_MEDIA  = '(max-width: 999px)';
  var SCROLL_DELTA  = 8;   // px — minimum move before toggling
  var SCROLL_TOP_SHOW_THRESHOLD = 80; // always show near top

  var nav;
  var badge;
  var lastY = 0;
  var ticking = false;
  var mediaQuery = window.matchMedia(MOBILE_MEDIA);

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ---------- Hide / show on scroll ------------------------------------ */
  function setHidden(hidden) {
    if (!nav) return;
    nav.setAttribute('data-hidden', hidden ? 'true' : 'false');
  }

  function handleScroll() {
    if (nav) setHidden(false);
    lastY = window.pageYOffset || 0;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(handleScroll);
      ticking = true;
    }
  }

  /* ---------- Cart count ----------------------------------------------- */
  function setCartCount(count) {
    if (!badge) return;
    var n = parseInt(count, 10);
    if (isNaN(n) || n < 0) n = 0;
    badge.setAttribute('data-count', String(n));
    badge.textContent = String(n);
    badge.setAttribute('aria-label', n + (n === 1 ? ' item' : ' items') + ' in cart');
    if (n > 0) {
      badge.removeAttribute('hidden');
    } else {
      badge.setAttribute('hidden', '');
    }
  }

  function readCountFromDetail(detail) {
    if (!detail) return null;
    // Prestige's cart event detail shape: { cart: { item_count: N, ... } }
    if (detail.cart && typeof detail.cart.item_count !== 'undefined') {
      return detail.cart.item_count;
    }
    // Defensive: some themes dispatch item_count at the root.
    if (typeof detail.item_count !== 'undefined') return detail.item_count;
    return null;
  }

  function fetchCartCount() {
    if (!window.fetch) return;
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + 'cart.js'
            : '/cart.js',
          { headers: { accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.item_count !== 'undefined') setCartCount(cart.item_count);
      })
      .catch(function () { /* swallow — non-critical */ });
  }

  /* ---------- Init ----------------------------------------------------- */
  function init() {
    nav   = document.querySelector('[data-bottom-nav]');
    badge = document.querySelector('.custom-bottom-nav__badge[data-cart-count]');

    if (!nav) return;

    lastY = window.pageYOffset || 0;

    // Scroll listener — passive for perf; only active on mobile breakpoint.
    window.addEventListener('scroll', onScroll, { passive: true });

    // Re-evaluate on breakpoint change (desktop should never hide).
    var onMQChange = function () {
      if (!mediaQuery.matches) setHidden(false);
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onMQChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(onMQChange);
    }

    // Cart event subscription — mirrors theme.js CartNotification pattern.
    document.addEventListener('cart:change', function (event) {
      var n = readCountFromDetail(event && event.detail);
      if (n !== null) setCartCount(n);
    });

    // `cart:refresh` signals that we should refetch the canonical cart.
    document.addEventListener('cart:refresh', function () {
      fetchCartCount();
    });

    // On bfcache restore, refetch to avoid stale counts.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) fetchCartCount();
    });
  }

  onReady(init);
})();

/* ==========================================================================
   Custom Sticky Mobile ATC controller
   - Show/hide via IntersectionObserver on the primary product-form submit.
   - Haptic tap (navigator.vibrate) on successful add-to-cart.
   - The submit button uses the `form` attribute to participate in Prestige's
     own <product-form> submit pipeline, which dispatches `cart:change` on
     success (theme.js CartNotification / ProductForm pattern). NOTE: the
     spec referred to this event as `cart:updated`; in Prestige's current
     theme.js the actual event name is `cart:change`, so we listen for that.
   ========================================================================== */

(function () {
  'use strict';

  var STICKY_MEDIA = '(max-width: 999px)';
  var bar;
  var primaryButton;
  var intersectionObserver;
  var mediaQuery = window.matchMedia(STICKY_MEDIA);

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function findPrimaryAtc() {
    // Primary ATC = first submit button inside a <product-form> that is NOT
    // inside our own sticky bar or Prestige's desktop <product-sticky-bar>.
    var candidates = document.querySelectorAll('product-form button[type="submit"]');
    for (var i = 0; i < candidates.length; i++) {
      var btn = candidates[i];
      if (btn.closest('.custom-sticky-atc-mobile')) continue;
      if (btn.closest('product-sticky-bar')) continue;
      return btn;
    }
    // Fallback: any form[action*="/cart/add"] submit not inside our bar.
    var fallbacks = document.querySelectorAll('form[action*="/cart/add"] button[type="submit"]');
    for (var j = 0; j < fallbacks.length; j++) {
      if (!fallbacks[j].closest('.custom-sticky-atc-mobile')) return fallbacks[j];
    }
    return null;
  }

  function setVisible(visible) {
    if (!bar) return;
    if (visible && mediaQuery.matches) {
      bar.classList.add('is-visible');
      bar.setAttribute('aria-hidden', 'false');
    } else {
      bar.classList.remove('is-visible');
      bar.setAttribute('aria-hidden', 'true');
    }
  }

  function observePrimary() {
    if (!primaryButton) return;
    if (!('IntersectionObserver' in window)) {
      // No IO support — just show when past a scroll threshold.
      setVisible(true);
      return;
    }
    if (intersectionObserver) intersectionObserver.disconnect();
    intersectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Show sticky bar only when the primary ATC is OFF-screen.
        setVisible(!entry.isIntersecting);
      });
    }, {
      root: null,
      threshold: 0,
      // Top margin: account for sticky header; bottom: account for our bar height.
      rootMargin: '-64px 0px -76px 0px'
    });
    intersectionObserver.observe(primaryButton);
  }

  function isOurSubmitEvent(event) {
    // `cart:change` bubbles on the documentElement from ProductForm. We use
    // the click-origin tracked on `bar` to confirm it was our submit.
    if (!bar) return false;
    return bar.hasAttribute('data-pending-submit');
  }

  function handleCartChange(event) {
    // Only vibrate if the add came from our sticky button. Clear the flag.
    if (isOurSubmitEvent(event)) {
      try {
        if (navigator.vibrate) navigator.vibrate(10);
      } catch (e) { /* ignore */ }
      bar.removeAttribute('data-pending-submit');
    }
    // Clear busy state on our button either way (cart changed → request done).
    var btn = bar && bar.querySelector('[data-custom-sticky-atc-submit]');
    if (btn) btn.removeAttribute('aria-busy');
  }

  function handleCartError() {
    if (!bar) return;
    bar.removeAttribute('data-pending-submit');
    var btn = bar.querySelector('[data-custom-sticky-atc-submit]');
    if (btn) btn.removeAttribute('aria-busy');
  }

  function wireSubmit() {
    if (!bar) return;
    var btn = bar.querySelector('[data-custom-sticky-atc-submit]');
    if (!btn) return;

    // Let the browser submit the associated form natively (the `form` attr
    // on the button does the binding). We only intercept to (a) tag the
    // submission so we can attribute the resulting cart:change to us, and
    // (b) reflect a busy state on our button.
    btn.addEventListener('click', function () {
      bar.setAttribute('data-pending-submit', '1');
      btn.setAttribute('aria-busy', 'true');
    });
  }

  function refreshTargets() {
    // Re-query DOM — <product-rerender> can replace the primary ATC when the
    // variant changes, which invalidates our observed node.
    primaryButton = findPrimaryAtc();
    observePrimary();
  }

  function init() {
    bar = document.querySelector('[data-custom-sticky-atc]');
    if (!bar) return;

    // If no bottom tab nav is on this page, sit flush to the bottom.
    if (!document.querySelector('[data-bottom-nav]')) {
      bar.setAttribute('data-no-bottom-nav', '');
    }

    primaryButton = findPrimaryAtc();
    if (!primaryButton) {
      // Primary ATC not in the DOM yet (unlikely) — try again shortly.
      setTimeout(refreshTargets, 400);
    } else {
      observePrimary();
    }

    wireSubmit();

    // Cart event subscription — listen to the SAME event Prestige's
    // <product-form> dispatches on a successful add (documentElement,
    // bubbles). See theme.js `onSubmit_fn` ~line 2917.
    document.addEventListener('cart:change', handleCartChange);
    document.addEventListener('cart:error', handleCartError);

    // If Prestige's product-rerender swaps the primary ATC node (variant
    // change rerenders the buy-buttons), re-observe.
    var mo = new MutationObserver(function () { refreshTargets(); });
    mo.observe(document.body, { childList: true, subtree: true });

    // Breakpoint hygiene.
    var onMQChange = function () {
      if (!mediaQuery.matches) setVisible(false);
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onMQChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(onMQChange);
    }
  }

  onReady(init);
})();

/* ==========================================================================
   Hebrew date strip populator
   - Loads assets/hebrew-calendar-2025-2028.json (baked by the theme
     build step — see gen_calendar.py in outputs).
   - Finds today's row, fills Hebrew date / active holiday / countdown /
     shipping cutoff into the strip placeholders.
   - Reconciles body[data-hebrew-phase] with the JSON's canonical value
     (Liquid renders an approximate phase server-side to avoid FOUC;
     the JSON is authoritative).
   ========================================================================== */

(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else { fn(); }
  }

  function todayISO() {
    var d = new Date();
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function setText(root, field, text) {
    var el = root.querySelector('[data-field="' + field + '"]');
    if (!el) return;
    el.textContent = text;
    el.removeAttribute('hidden');
    // Also show the preceding separator if there is one for this field.
    var sep = root.querySelector('[data-field="' + field + '-sep"]');
    if (sep) sep.removeAttribute('hidden');
  }

  function hideField(root, field) {
    var el = root.querySelector('[data-field="' + field + '"]');
    if (el) el.setAttribute('hidden', '');
    var sep = root.querySelector('[data-field="' + field + '-sep"]');
    if (sep) sep.setAttribute('hidden', '');
  }

  function formatCountdown(next) {
    if (!next) return null;
    if (next.days === 0) return null; // active holiday covers this
    if (next.days === 1) return '1 day to ' + next.label;
    return next.days + ' days to ' + next.label;
  }

  function populate(root, row) {
    if (!row) return;
    setText(root, 'hebrew', row.hebrew_short || row.hebrew || '');

    if (row.active_holiday && row.active_holiday.english) {
      setText(root, 'active-holiday', row.active_holiday.english);
    } else {
      hideField(root, 'active-holiday');
    }

    var cd = formatCountdown(row.next_chag);
    if (cd) {
      setText(root, 'countdown', cd);
    } else {
      hideField(root, 'countdown');
    }

    // Delivery-date promises are intentionally suppressed. Store policy permits
    // service windows only, not customer-facing arrival guarantees.
    hideField(root, 'cutoff');

    // Reconcile phase with authoritative JSON value.
    if (row.holiday_phase) {
      document.body.setAttribute('data-hebrew-phase', row.holiday_phase);
      root.setAttribute('data-phase', row.holiday_phase);
    }
  }

  function init() {
    var root = document.querySelector('[data-hebrew-date-strip]');
    if (!root) return;

    var url = root.getAttribute('data-hebrew-calendar-url');
    if (!url) return;

    // Cache aggressively — the JSON is static for the theme's lifetime.
    fetch(url, { cache: 'force-cache' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (rows) {
        if (!Array.isArray(rows)) return;
        var iso = todayISO();
        // Binary-ish search: rows are sorted by date ascending, but linear
        // find is fine at ~1500 rows.
        var row = null;
        for (var i = 0; i < rows.length; i++) {
          if (rows[i].date === iso) { row = rows[i]; break; }
        }
        populate(root, row);
      })
      .catch(function () { /* non-critical — leave skeleton */ });
  }

  onReady(init);
})();

/* ==========================================================================
   4. Free-shipping progress bar updater
   --------------------------------------------------------------------------
   Prestige already mutates <free-shipping-bar total-price="..."> on every
   `cart:change` event (see theme.js FreeShippingBar). We watch that
   attribute with a MutationObserver and update the visual fill width +
   is-complete class. No cart API calls — we piggyback on the data Prestige
   has already done the work to compute.
   ========================================================================== */
(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function updateFill(wrapper) {
    if (!wrapper) return;
    var fsb = wrapper.querySelector('free-shipping-bar');
    if (!fsb) return;

    var total = parseFloat(fsb.getAttribute('total-price'));
    var threshold = parseFloat(fsb.getAttribute('threshold'));
    if (!isFinite(total) || !isFinite(threshold) || threshold <= 0) return;

    var pct = Math.max(0, Math.min(100, (total / threshold) * 100));
    var fill = wrapper.querySelector('[data-custom-free-shipping-bar-fill]');
    if (fill) {
      fill.style.width = pct + '%';
    }

    if (pct >= 100) {
      wrapper.classList.add('is-complete');
    } else {
      wrapper.classList.remove('is-complete');
    }
  }

  function attach(wrapper) {
    var fsb = wrapper.querySelector('free-shipping-bar');
    if (!fsb) return;

    // Observe the <free-shipping-bar> for total-price changes.
    var observer = new MutationObserver(function () { updateFill(wrapper); });
    observer.observe(fsb, { attributes: true, attributeFilter: ['total-price', 'threshold'] });

    // Belt-and-braces: also listen to cart:change directly in case Prestige
    // rerenders the wrapper (which would replace the <free-shipping-bar>
    // element and disconnect the original observer).
    document.addEventListener('cart:change', function () {
      window.requestAnimationFrame(function () { updateFill(wrapper); });
    });

    // Initial paint (in case Liquid-rendered % is stale after a rerender)
    updateFill(wrapper);
  }

  function init() {
    document.querySelectorAll('[data-custom-free-shipping-bar]').forEach(attach);

    // Prestige rerenders cart-drawer contents on cart change — pick up any
    // newly-inserted wrappers that weren't there at init time.
    var bodyObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-custom-free-shipping-bar]')) {
            attach(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('[data-custom-free-shipping-bar]').forEach(attach);
          }
        });
      });
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  onReady(init);
})();

/* ============================================================================
 * SECTION 5 — AR view button
 * Finds the <model-viewer> that Prestige renders inside the gallery's
 * <model-media> element and calls activateAR() on it. No extra script loads
 * because Prestige already bundles model-viewer when a 3D media is present.
 * If the user clicks before the gallery has hydrated, we scroll to the
 * gallery as a graceful fallback.
 * ========================================================================*/
(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function findModelViewer() {
    // Prestige wraps its model-viewer inside <model-media>; the element is
    // globally unique per PDP (one visible at a time).
    var mv = document.querySelector('model-media model-viewer, model-viewer');
    return mv || null;
  }

  function waitForModelViewer(timeoutMs) {
    return new Promise(function (resolve) {
      var mv = findModelViewer();
      if (mv) return resolve(mv);
      var start = Date.now();
      var interval = setInterval(function () {
        mv = findModelViewer();
        if (mv) {
          clearInterval(interval);
          resolve(mv);
        } else if (Date.now() - start > timeoutMs) {
          clearInterval(interval);
          resolve(null);
        }
      }, 100);
    });
  }

  function activateAR(button) {
    button.classList.add('is-loading');
    waitForModelViewer(3000).then(function (mv) {
      button.classList.remove('is-loading');
      if (!mv) {
        // Fallback: scroll to gallery so the user can tap the native AR icon.
        var gallery = document.querySelector('product-gallery, .product-gallery, [data-product-gallery]');
        if (gallery && gallery.scrollIntoView) {
          gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
      // model-viewer exposes activateAR() on supported devices (iOS Safari,
      // Android Chrome). On desktop it no-ops; we still scroll to the gallery
      // so the user sees the 3D model.
      try {
        if (typeof mv.activateAR === 'function' && mv.canActivateAR) {
          mv.activateAR();
        } else if (mv.scrollIntoView) {
          mv.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof mv.play === 'function') mv.play();
        }
      } catch (e) {
        if (mv.scrollIntoView) mv.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  function attach(button) {
    if (button.__arAttached) return;
    button.__arAttached = true;
    button.addEventListener('click', function () { activateAR(button); });
  }

  function init() {
    document.querySelectorAll('[data-pdp-ar-button]').forEach(attach);

    // Re-scan on product rerender (variant swap on Prestige fires this).
    document.addEventListener('product:rerender', function () {
      document.querySelectorAll('[data-pdp-ar-button]').forEach(attach);
    });

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-pdp-ar-button]')) attach(node);
          else if (node.querySelectorAll) node.querySelectorAll('[data-pdp-ar-button]').forEach(attach);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  onReady(init);
})();

/* SPRINT_1_EDITORIAL_START */
/* ==========================================================================
   Sprint 1 â Editorial mobile redesign behavior
   Appended to assets/custom.js.
   Handles: Shop-tab slide-up category drawer, Search overlay with predictive,
   body scroll lock, swipe-down dismiss.
   ========================================================================== */

(function () {
  'use strict';

  const BODY = document.body;
  const HTML = document.documentElement;
  const LOCK_ATTR = 'data-js-lock';

  function lockScroll() { BODY.setAttribute(LOCK_ATTR, 'true'); }
  function unlockScroll() { BODY.removeAttribute(LOCK_ATTR); }

  // ---------- Shop drawer ----------

  function openDrawer() {
    const backdrop = document.querySelector('[data-js-backdrop="shop"]');
    const drawer = document.querySelector('[data-js-shop-drawer]');
    if (!drawer || !backdrop) return;
    backdrop.dataset.open = 'true';
    drawer.dataset.open = 'true';
    lockScroll();
  }

  function closeDrawer() {
    const backdrop = document.querySelector('[data-js-backdrop="shop"]');
    const drawer = document.querySelector('[data-js-shop-drawer]');
    if (!drawer || !backdrop) return;
    backdrop.dataset.open = 'false';
    drawer.dataset.open = 'false';
    unlockScroll();
  }

  function wireShopTab() {
    const shopTab = document.querySelector('[data-nav-item="shop"]');
    if (!shopTab) return;
    shopTab.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer();
    });

    document.addEventListener('click', function (e) {
      if (e.target.matches('[data-js-backdrop="shop"]')) closeDrawer();
      if (e.target.closest('[data-js-drawer-close]')) closeDrawer();
    });

    // swipe down to close
    const drawer = document.querySelector('[data-js-shop-drawer]');
    if (drawer) {
      let startY = null;
      drawer.addEventListener('touchstart', function (e) {
        if (drawer.scrollTop === 0) startY = e.touches[0].clientY;
      }, { passive: true });
      drawer.addEventListener('touchmove', function (e) {
        if (startY === null) return;
        const dy = e.touches[0].clientY - startY;
        if (dy > 80) { closeDrawer(); startY = null; }
      }, { passive: true });
      drawer.addEventListener('touchend', function () { startY = null; });
    }
  }

  // ---------- Search overlay ----------

  let searchTimer = null;
  const RECENT_KEY_COOKIE = 'js_recent_searches';

  function getRecentSearches() {
    try {
      const m = document.cookie.match(/js_recent_searches=([^;]+)/);
      if (!m) return [];
      return JSON.parse(decodeURIComponent(m[1])).slice(0, 6);
    } catch (_) { return []; }
  }

  function saveRecentSearch(term) {
    if (!term) return;
    const list = getRecentSearches().filter(t => t !== term);
    list.unshift(term);
    const trimmed = list.slice(0, 6);
    document.cookie = 'js_recent_searches=' + encodeURIComponent(JSON.stringify(trimmed)) + '; path=/; max-age=2592000; SameSite=Lax';
  }

  function openSearch() {
    const overlay = document.querySelector('[data-js-search-overlay]');
    if (!overlay) return;
    overlay.dataset.open = 'true';
    lockScroll();
    renderEmptyState();
    const input = overlay.querySelector('[data-js-search-input]');
    if (input) {
      input.value = '';
      setTimeout(() => input.focus(), 200);
    }
  }

  function closeSearch() {
    const overlay = document.querySelector('[data-js-search-overlay]');
    if (!overlay) return;
    overlay.dataset.open = 'false';
    unlockScroll();
  }

  function renderEmptyState() {
    const body = document.querySelector('[data-js-search-body]');
    if (!body) return;
    const recents = getRecentSearches();
    let html = '';
    if (recents.length) {
      html += '<div class="js-search-overlay__group-title">Recent</div>';
      html += '<div class="js-search-overlay__recent">';
      recents.forEach(t => { html += '<button type="button" data-js-recent="' + encodeHTML(t) + '">' + encodeHTML(t) + '</button>'; });
      html += '</div>';
    } else {
      html = '<p class="js-search-overlay__empty">Begin typing to search the library.</p>';
    }
    body.innerHTML = html;
  }

  function encodeHTML(s) {
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'})[c]);
  }

  function renderResults(data, term) {
    const body = document.querySelector('[data-js-search-body]');
    if (!body) return;
    const res = (data && data.resources && data.resources.results) || {};
    const products = res.products || [];
    const collections = res.collections || [];
    const pages = res.pages || [];

    if (!products.length && !collections.length && !pages.length) {
      body.innerHTML = '<p class="js-search-overlay__empty">Nothing matches &ldquo;' + encodeHTML(term) + '&rdquo; &mdash; yet.</p>';
      return;
    }

    let html = '';
    if (products.length) {
      html += '<div class="js-search-overlay__group-title">Products</div>';
      products.slice(0, 6).forEach(p => {
        const img = p.featured_image && p.featured_image.url ? '<img src="' + encodeHTML(p.featured_image.url) + '" alt="">' : '<div style="width:48px;height:48px;background:var(--js-parchment-deep);"></div>';
        const price = p.price ? formatMoney(p.price) : '';
        html += '<a class="js-search-overlay__result" href="' + encodeHTML(p.url) + '">' + img + '<span class="js-search-overlay__result-title">' + encodeHTML(p.title) + '</span><span class="js-search-overlay__result-price">' + price + '</span></a>';
      });
    }
    if (collections.length) {
      html += '<div class="js-search-overlay__group-title">Collections</div>';
      collections.slice(0, 4).forEach(c => {
        html += '<a class="js-search-overlay__result" href="' + encodeHTML(c.url) + '"><span class="js-search-overlay__result-title">' + encodeHTML(c.title) + '</span></a>';
      });
    }
    if (pages.length) {
      html += '<div class="js-search-overlay__group-title">Pages</div>';
      pages.slice(0, 4).forEach(pg => {
        html += '<a class="js-search-overlay__result" href="' + encodeHTML(pg.url) + '"><span class="js-search-overlay__result-title">' + encodeHTML(pg.title) + '</span></a>';
      });
    }
    body.innerHTML = html;
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toFixed(2);
  }

  function runPredictive(term) {
    if (!term || term.length < 2) { renderEmptyState(); return; }
    /* SEARCH_SKU_FIX 2026-05-03 — add fields=...,body,variants.barcode so SKU codes like c805 surface in mobile predictive */
    const url = '/search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku,body,variants.barcode';
    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(d => renderResults(d, term))
      .catch(() => {
        const body = document.querySelector('[data-js-search-body]');
        if (body) body.innerHTML = '<p class="js-search-overlay__empty">Couldn\u2019t reach search. Try again.</p>';
      });
  }

  function wireSearch() {
    const searchTab = document.querySelector('[data-nav-item="search"]');
    if (searchTab) {
      searchTab.addEventListener('click', function (e) {
        e.preventDefault();
        openSearch();
      });
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-js-search-close]')) { closeSearch(); return; }
      const recent = e.target.closest('[data-js-recent]');
      if (recent) {
        const input = document.querySelector('[data-js-search-input]');
        if (input) { input.value = recent.dataset.jsRecent; input.dispatchEvent(new Event('input', { bubbles: true })); }
      }
    });

    const input = document.querySelector('[data-js-search-input]');
    if (input) {
      input.addEventListener('input', function (e) {
        const term = e.target.value.trim();
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => runPredictive(term), 160);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          const term = e.target.value.trim();
          if (term) {
            saveRecentSearch(term);
            window.location.href = '/search?q=' + encodeURIComponent(term);
          }
        }
      });
    }

    // swipe down on overlay head to close
    const overlay = document.querySelector('[data-js-search-overlay]');
    if (overlay) {
      let startY = null;
      overlay.addEventListener('touchstart', function (e) {
        if (overlay.scrollTop === 0) startY = e.touches[0].clientY;
      }, { passive: true });
      overlay.addEventListener('touchmove', function (e) {
        if (startY === null) return;
        const dy = e.touches[0].clientY - startY;
        if (dy > 100) { closeSearch(); startY = null; }
      }, { passive: true });
      overlay.addEventListener('touchend', function () { startY = null; });
    }
  }

  // ---------- Boot ----------

  function boot() {
    wireShopTab();
    wireSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* SPRINT_1_EDITORIAL_END */

/* SPRINT_2_5_EDITORIAL_START */
/* ==========================================================================
   Sprint 2-5 — Editorial homepage / collection / PDP / polish behavior
   Appended to assets/custom.js.
   Handles: quick-add cart, gallery dots, variant chips, sticky ATC,
   filter/sort bottom sheets, iOS soft-keyboard pinning, reduced-motion,
   toast notifications, haptic feedback.
   ========================================================================== */

(function () {
  'use strict';

  var BODY = document.body;
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function haptic(ms) {
    try { if (navigator.vibrate && !REDUCED_MOTION) navigator.vibrate(ms || 8); } catch(_) {}
  }

  function money(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toFixed(2);
  }

  // ---------- toast ----------
  function toast(msg) {
    var el = document.querySelector('.js-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'js-toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.setAttribute('data-show', 'true');
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.setAttribute('data-show', 'false'); }, 2400);
  }

  // ---------- quick-add (home + collection cards) ----------
  function wireQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-js-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var id = btn.getAttribute('data-variant-id');
      if (!id) return;
      btn.setAttribute('data-loading', 'true');
      var old = btn.textContent;
      btn.textContent = '…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: id, quantity: 1 }),
      }).then(function (r) { return r.json(); }).then(function (j) {
        btn.textContent = '✓';
        haptic(12);
        toast('Added to cart');
        // refresh cart count badge
        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
          var badge = document.querySelector('[data-cart-count]');
          if (badge) {
            badge.textContent = c.item_count;
            if (c.item_count > 0) badge.removeAttribute('hidden'); else badge.setAttribute('hidden', '');
          }
          document.dispatchEvent(new CustomEvent('cart:change', { detail: c }));
        });
        setTimeout(function () { btn.textContent = old; btn.removeAttribute('data-loading'); }, 1200);
      }).catch(function () {
        btn.textContent = '!';
        toast('Couldn\u2019t add — try again');
        setTimeout(function () { btn.textContent = old; btn.removeAttribute('data-loading'); }, 1600);
      });
    });
  }

  // ---------- heart/save (collection cards) ----------
  function wireHearts() {
    document.addEventListener('click', function (e) {
      var h = e.target.closest('[data-js-heart]');
      if (!h) return;
      e.preventDefault();
      e.stopPropagation();
      var pressed = h.getAttribute('aria-pressed') === 'true';
      h.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      h.textContent = pressed ? '♡' : '♥';
      haptic(6);
    });
  }

  // ---------- collection filter / sort sheets ----------
  function openFilter() {
    var bk = document.querySelector('[data-js-filter-backdrop]');
    var sh = document.querySelector('[data-js-filter-sheet]');
    if (!bk || !sh) return;
    bk.setAttribute('data-open', 'true');
    sh.setAttribute('data-open', 'true');
    BODY.setAttribute('data-js-lock', 'true');
  }

  function closeFilter() {
    var bk = document.querySelector('[data-js-filter-backdrop]');
    var sh = document.querySelector('[data-js-filter-sheet]');
    if (!bk || !sh) return;
    bk.setAttribute('data-open', 'false');
    sh.setAttribute('data-open', 'false');
    BODY.removeAttribute('data-js-lock');
  }

  function openSort() {
    var sh = document.querySelector('[data-js-sort-sheet]');
    var bk = document.querySelector('[data-js-filter-backdrop]');
    if (!sh) return;
    if (bk) bk.setAttribute('data-open', 'true');
    sh.setAttribute('data-open', 'true');
    BODY.setAttribute('data-js-lock', 'true');
  }

  function closeSort() {
    var sh = document.querySelector('[data-js-sort-sheet]');
    var bk = document.querySelector('[data-js-filter-backdrop]');
    if (!sh) return;
    sh.setAttribute('data-open', 'false');
    if (bk && !document.querySelector('[data-js-filter-sheet][data-open="true"]')) {
      bk.setAttribute('data-open', 'false');
      BODY.removeAttribute('data-js-lock');
    }
  }

  function wireCollectionSheets() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-js-open-filter]')) { e.preventDefault(); openFilter(); }
      if (e.target.closest('[data-js-filter-close]')) { e.preventDefault(); closeFilter(); }
      if (e.target.closest('[data-js-open-sort]'))   { e.preventDefault(); openSort(); }
      if (e.target.closest('[data-js-sort-close]'))  { e.preventDefault(); closeSort(); }
      if (e.target.matches('[data-js-filter-backdrop]')) { closeFilter(); closeSort(); }
      var sortOpt = e.target.closest('[data-js-sort]');
      if (sortOpt) {
        e.preventDefault();
        var val = sortOpt.getAttribute('data-js-sort');
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', val);
        window.location.href = url.toString();
      }
    });

    // update filter count chip
    var form = document.querySelector('[data-js-filter-form]');
    if (form) {
      function updateCount() {
        var checked = form.querySelectorAll('input[type="checkbox"]:checked').length;
        var range = form.querySelectorAll('input[type="number"]');
        var hasPrice = Array.from(range).some(function (i) { return i.value && i.value !== i.placeholder; });
        var total = checked + (hasPrice ? 1 : 0);
        var chip = document.querySelector('[data-js-filter-count]');
        if (chip) {
          chip.textContent = total;
          if (total) chip.removeAttribute('hidden'); else chip.setAttribute('hidden', '');
        }
      }
      form.addEventListener('change', updateCount);
      updateCount();
    }
  }

  // ---------- PDP: gallery dots, variant selection, sticky ATC ----------
  function wirePdpGallery() {
    var track = document.querySelector('[data-js-gallery-track]');
    if (!track) return;
    var dots = Array.from(document.querySelectorAll('[data-js-dot]'));
    if (!dots.length) return;
    function sync() {
      var idx = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach(function (d, i) { d.classList.toggle('js-pdp__dot--active', i === idx); });
    }
    track.addEventListener('scroll', sync, { passive: true });
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () {
        track.scrollTo({ left: i * track.clientWidth, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
      });
    });
  }

  function wirePdpVariants() {
    var form = document.querySelector('[data-js-variants]');
    if (!form) return;
    var productEl = document.querySelector('[data-js-pdp]');
    var productId = productEl && productEl.getAttribute('data-product-id');
    if (!productId) return;

    // build variant lookup from ShopifyAnalytics or fetch product.js
    var variants = null;
    function ensureVariants() {
      if (variants) return Promise.resolve(variants);
      return fetch('/products/' + (window.__pdpHandle || location.pathname.split('/').pop()) + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) { variants = p.variants; window.__pdpProduct = p; return variants; });
    }

    function pickVariant() {
      var chosen = [];
      var options = form.querySelectorAll('.js-pdp__option');
      options.forEach(function (o) {
        var sel = o.querySelector('input[type="radio"]:checked');
        chosen.push(sel ? sel.value : null);
      });
      ensureVariants().then(function (vs) {
        var match = vs.find(function (v) {
          return chosen.every(function (val, i) { return !val || v.options[i] === val; });
        });
        if (!match) return;
        var idInp = form.querySelector('[data-js-variant-id]');
        if (idInp) idInp.value = match.id;
        var pNow = document.querySelector('.js-pdp__price-now');
        if (pNow) pNow.textContent = money(match.price);
        var atcPrice = document.querySelector('[data-js-atc-price]');
        if (atcPrice) atcPrice.textContent = money(match.price);
        var stickyPrice = document.querySelector('[data-js-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = money(match.price);
      });
    }

    form.addEventListener('change', pickVariant);

    // qty stepper
    form.addEventListener('click', function (e) {
      var b = e.target.closest('[data-js-qty]');
      if (!b) return;
      e.preventDefault();
      var inp = form.querySelector('[data-js-qty-input]');
      if (!inp) return;
      var delta = parseInt(b.getAttribute('data-js-qty'), 10);
      var v = parseInt(inp.value, 10) || 1;
      inp.value = Math.max(1, v + delta);
    });
  }

  function wirePdpAtc() {
    var atc = document.querySelector('[data-js-atc]');
    var stickyAtc = document.querySelector('[data-js-sticky-atc]');
    function addCurrent() {
      var form = document.querySelector('[data-js-variants]');
      var id = form ? form.querySelector('[data-js-variant-id]').value : null;
      var qty = form ? parseInt(form.querySelector('[data-js-qty-input]').value || 1, 10) : 1;
      if (!id) {
        // no variants: pull from product.json
        fetch('/products/' + location.pathname.split('/').pop() + '.js')
          .then(function (r) { return r.json(); })
          .then(function (p) {
            id = p.variants[0].id;
            doAdd(id, 1);
          });
      } else {
        doAdd(id, qty);
      }
    }
    function doAdd(id, qty) {
      if (atc) atc.setAttribute('data-loading', 'true');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: id, quantity: qty }),
      }).then(function (r) { return r.json(); }).then(function () {
        haptic(14);
        toast('Added to cart');
        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
          var badge = document.querySelector('[data-cart-count]');
          if (badge) {
            badge.textContent = c.item_count;
            if (c.item_count > 0) badge.removeAttribute('hidden'); else badge.setAttribute('hidden', '');
          }
        });
      }).catch(function () {
        toast('Couldn\u2019t add — try again');
      }).then(function () {
        if (atc) atc.removeAttribute('data-loading');
      });
    }
    if (atc) atc.addEventListener('click', function (e) { e.preventDefault(); addCurrent(); });
    if (stickyAtc) stickyAtc.addEventListener('click', function (e) { e.preventDefault(); addCurrent(); });

    var buynow = document.querySelector('[data-js-buynow]');
    if (buynow) {
      buynow.addEventListener('click', function (e) {
        e.preventDefault();
        var form = document.querySelector('[data-js-variants]');
        var id = form ? form.querySelector('[data-js-variant-id]').value : null;
        var qty = form ? parseInt(form.querySelector('[data-js-qty-input]').value || 1, 10) : 1;
        if (!id) {
          window.location.href = '/checkout';
          return;
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: id, quantity: qty }),
        }).then(function () { window.location.href = '/checkout'; });
      });
    }
  }

  function wirePdpStickyAtc() {
    var sticky = document.querySelector('[data-js-pdp-sticky]');
    var anchor = document.querySelector('[data-js-buy]');
    if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        sticky.setAttribute('data-visible', en.isIntersecting ? 'false' : 'true');
      });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  // ---------- iOS soft-keyboard pinning for search overlay ----------
  function wireKeyboardPin() {
    if (!window.visualViewport) return;
    var overlay = document.querySelector('[data-js-search-overlay]');
    if (!overlay) return;
    function update() {
      if (overlay.getAttribute('data-open') !== 'true') return;
      var vv = window.visualViewport;
      var bodyEl = overlay.querySelector('[data-js-search-body]');
      if (bodyEl) {
        bodyEl.style.maxHeight = vv.height - 56 + 'px';
      }
    }
    window.visualViewport.addEventListener('resize', update);
    window.visualViewport.addEventListener('scroll', update);
  }

  // ---------- boot ----------
  function boot() {
    wireQuickAdd();
    wireHearts();
    wireCollectionSheets();
    wirePdpGallery();
    wirePdpVariants();
    wirePdpAtc();
    wirePdpStickyAtc();
    wireKeyboardPin();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* SPRINT_2_5_EDITORIAL_END */

/* SPRINT_2_5_HEADER_MENU_START */
(function(){
  function init(){
    var btn = document.querySelector('[data-ed-open-menu]');
    var panel = document.querySelector('[data-ed-menu-panel]');
    var back = document.querySelector('[data-ed-menu-backdrop]');
    var close = document.querySelector('[data-ed-close-menu]');
    if (!btn || !panel || !back) return;
    function open(){
      panel.classList.add('is-open');
      back.classList.add('is-open');
      panel.setAttribute('aria-hidden','false');
      back.setAttribute('aria-hidden','false');
      btn.setAttribute('aria-expanded','true');
      document.body.classList.add('ed-menu-open');
    }
    function shut(){
      panel.classList.remove('is-open');
      back.classList.remove('is-open');
      panel.setAttribute('aria-hidden','true');
      back.setAttribute('aria-hidden','true');
      btn.setAttribute('aria-expanded','false');
      document.body.classList.remove('ed-menu-open');
    }
    btn.addEventListener('click', function(e){ e.preventDefault(); open(); });
    if (close) close.addEventListener('click', shut);
    back.addEventListener('click', shut);
    document.addEventListener('keydown', function(e){ if (e.key === 'Escape') shut(); });
    // Live cart count update
    document.addEventListener('cart:change', function(e){
      var c = document.querySelector('[data-ed-cart-count]');
      if (!c || !e.detail) return;
      var n = e.detail.cart && e.detail.cart.item_count;
      if (typeof n === 'number'){ c.textContent = n; if (n>0) c.removeAttribute('hidden'); else c.setAttribute('hidden','hidden'); }
    });
    // Fallback: poll /cart.js on load
    try {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(r){ return r.json(); })
        .then(function(j){
          var c = document.querySelector('[data-ed-cart-count]');
          if (c && typeof j.item_count === 'number'){
            c.textContent = j.item_count;
            if (j.item_count > 0) c.removeAttribute('hidden'); else c.setAttribute('hidden','hidden');
          }
        }).catch(function(){});
    } catch(e){}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
/* SPRINT_2_5_HEADER_MENU_END */

/* SPRINT_2_6_CART_JS_START */
(function(){
  function moneyFmt(cents, theme){
    return (window.Shopify && Shopify.formatMoney) ? Shopify.formatMoney(cents, theme || '') :
      '$' + (cents/100).toFixed(2);
  }
  function fetchCart(){
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }})
      .then(function(r){ return r.json(); });
  }
  function updateLine(line, qty){
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: qty })
    }).then(function(r){ return r.json(); });
  }
  function refreshHeaderCount(count){
    var cnt = document.querySelector('[data-ed-cart-count]');
    if (!cnt) return;
    cnt.textContent = count;
    if (count > 0) cnt.removeAttribute('hidden'); else cnt.setAttribute('hidden','hidden');
  }
  function onQtyChange(input){
    var line = Number(input.getAttribute('data-line'));
    var qty = Math.max(0, Number(input.value || 0));
    var itemEl = input.closest('[data-ed-cart-item]');
    if (itemEl) itemEl.classList.add('ed-cart__item--leaving');
    updateLine(line, qty).then(function(cart){
      refreshHeaderCount(cart.item_count);
      if (qty === 0){
        if (itemEl){
          setTimeout(function(){
            itemEl.remove();
            if (!document.querySelector('[data-ed-cart-item]')) window.location.reload();
          }, 220);
        } else {
          window.location.reload();
        }
      } else {
        var sub = document.querySelector('[data-ed-subtotal]');
        if (sub) sub.textContent = moneyFmt(cart.items_subtotal_price);
        if (itemEl) itemEl.classList.remove('ed-cart__item--leaving');
        // Update line price
        var price = itemEl && itemEl.querySelector('.ed-cart__price');
        var item = cart.items[line - 1];
        if (price && item) price.textContent = moneyFmt(item.final_line_price);
        // Update item count in title
        var title = document.querySelector('.ed-cart__title');
        if (title) title.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
      }
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart }}));
    }).catch(function(){
      if (itemEl) itemEl.classList.remove('ed-cart__item--leaving');
    });
  }
  function init(){
    var form = document.querySelector('.ed-cart__form');
    if (!form) return;
    form.addEventListener('click', function(e){
      var dec = e.target.closest('[data-ed-qty-dec]');
      var inc = e.target.closest('[data-ed-qty-inc]');
      var rm  = e.target.closest('[data-ed-remove]');
      if (dec || inc){
        var qtyWrap = e.target.closest('[data-ed-qty]');
        if (!qtyWrap) return;
        var input = qtyWrap.querySelector('[data-ed-qty-input]');
        var v = Math.max(0, Number(input.value || 0) + (dec ? -1 : 1));
        input.value = v;
        onQtyChange(input);
      } else if (rm){
        e.preventDefault();
        var line = Number(rm.getAttribute('data-line'));
        var input = form.querySelector('[data-ed-qty-input][data-line="' + line + '"]');
        if (input){ input.value = 0; onQtyChange(input); }
      }
    });
    form.addEventListener('change', function(e){
      if (e.target.matches('[data-ed-qty-input]')) onQtyChange(e.target);
    });
    // Save note on blur
    var note = form.querySelector('.ed-cart__note');
    if (note){
      note.addEventListener('blur', function(){
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ note: note.value })
        });
      });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
/* SPRINT_2_6_CART_JS_END */



/* =====================================================================
   DESKTOP AUDIT PATCHES — 2026-04-23
   Small JS cleanups appended by audit pass. Safe to revert.
   ===================================================================== */
(function() {
  // C1: remove the stray "/" text node child of <x-header> (between <nav> and <header-search>)
  function stripStraySlashInHeader() {
    try {
      document.querySelectorAll('x-header').forEach(function(h) {
        Array.from(h.childNodes).forEach(function(n) {
          if (n.nodeType === 3 && n.textContent && n.textContent.trim() === '/') {
            n.remove();
          }
        });
      });
    } catch (e) {}
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stripStraySlashInHeader);
  } else {
    stripStraySlashInHeader();
  }
  // Observe for x-header mount (header is custom element, may not exist at DCL)
  try {
    new MutationObserver(function(muts) { stripStraySlashInHeader(); }).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}
})();
/* ===================== END DESKTOP AUDIT PATCHES ===================== */


/* ==========================================================================
   Live Shavuos countdown — belt-and-suspenders client-side updater.
   Keeps the hero eyebrow (.js-hero__eyebrow) accurate even if a cached
   page was served. Runs once on DOM ready. Matches either the {days}
   placeholder or any hard-coded 'NN DAYS TO SHAVUOS' pattern.
   ==========================================================================*/
(function () {
  'use strict';
  var SHAVUOS_DATES = ['2026-05-22', '2027-05-12', '2028-05-31'];
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else { fn(); }
  }
  function daysUntilNextShavuos() {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    for (var i = 0; i < SHAVUOS_DATES.length; i++) {
      var p = SHAVUOS_DATES[i].split('-');
      var t = new Date(+p[0], +p[1] - 1, +p[2]);
      t.setHours(0, 0, 0, 0);
      var diff = Math.round((t - today) / 86400000);
      if (diff >= 0) return diff;
    }
    return null;
  }
  onReady(function () {
    var el = document.querySelector('.js-hero__eyebrow');
    if (!el) return;
    var days = daysUntilNextShavuos();
    if (days == null) return;
    var text = el.textContent;
    if (text.indexOf('{days}') !== -1) {
      el.textContent = text.replace('{days}', String(days));
      return;
    }
    var m = /(\d+)(\s+DAYS\s+TO\s+SHAVUOS)/i.exec(text);
    if (m) el.textContent = text.replace(m[0], String(days) + m[2]);
  });
})();


/* SEARCH_SKU_FIX_DESKTOP_PRESTIGE 2026-05-03 — intercept Prestige's predictive-search component
   so SKU codes like 'c805' surface in the Prestige header search dialog (which uses /search/suggest
   Liquid section render rather than /search/suggest.json). Same fields fix as desktop .jz-hdr.   */
(function() {
  'use strict';
  if (window.__JZ_PREDICTIVE_FIELDS_PATCH) return;
  window.__JZ_PREDICTIVE_FIELDS_PATCH = true;
  var FIELDS = 'title,vendor,product_type,variants.title,variants.sku,body,variants.barcode';
  var TAG = '[JZ_SEARCH_FIX]';
  console.log(TAG + ' patch installed');

  function rewriteUrl(urlStr) {
    if (!urlStr || urlStr.indexOf('/search/suggest') === -1) return null;
    if (urlStr.indexOf('resources%5Boptions%5D%5Bfields%5D') !== -1) return null;
    if (urlStr.indexOf('resources[options][fields]') !== -1) return null;
    var sep = urlStr.indexOf('?') === -1 ? '?' : '&';
    var newUrl = urlStr + sep + 'resources[options][fields]=' + encodeURIComponent(FIELDS);
    console.log(TAG + ' rewrote', urlStr.length > 80 ? urlStr.slice(0,80)+'...' : urlStr, '->', newUrl.slice(-120));
    return newUrl;
  }

  // Patch window.fetch
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function(input, init) {
      try {
        var urlStr = typeof input === 'string' ? input : (input && input.url) || '';
        var newUrl = rewriteUrl(urlStr);
        if (newUrl) {
          if (typeof input === 'string') {
            input = newUrl;
          } else if (typeof Request !== 'undefined' && input instanceof Request) {
            input = new Request(newUrl, input);
          }
        }
      } catch (e) { console.warn(TAG + ' fetch wrap err', e); }
      return origFetch.call(this, input, init);
    };
  }

  // Also patch XMLHttpRequest.open in case anything uses XHR
  try {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      try {
        var newUrl = rewriteUrl(url);
        if (newUrl) {
          arguments[1] = newUrl;
        }
      } catch (e) {}
      return origOpen.apply(this, arguments);
    };
  } catch (e) {}
})();


/* SPRINT4.4 (2026-07-05) — shared focus trap + focus restore for the three overlay dialogs:
   #cart-drawer (opens via .is-open), the ikippah filter drawer ([data-open="true"]), and the
   mobile search overlay ([data-open="true"]). Tab/Shift+Tab cycle inside the open dialog;
   when it closes, focus returns to the element that opened it. Escape handling already
   exists in each component. */
(function () {
  'use strict';
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function visible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
  function watch(el, isOpen) {
    var restoreTo = null, trapHandler = null, wasOpen = false;
    function activate() {
      restoreTo = document.activeElement;
      trapHandler = function (e) {
        if (e.key !== 'Tab') return;
        var items = [].slice.call(el.querySelectorAll(FOCUSABLE)).filter(visible);
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || !el.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', trapHandler, true);
      if (!el.contains(document.activeElement)) {
        var items = [].slice.call(el.querySelectorAll(FOCUSABLE)).filter(visible);
        if (items.length) { try { items[0].focus(); } catch (err) {} }
      }
    }
    function deactivate() {
      if (trapHandler) { document.removeEventListener('keydown', trapHandler, true); trapHandler = null; }
      if (restoreTo && document.contains(restoreTo) && visible(restoreTo)) { try { restoreTo.focus(); } catch (err) {} }
      restoreTo = null;
    }
    new MutationObserver(function () {
      var openNow = isOpen(el);
      if (openNow && !wasOpen) activate();
      else if (!openNow && wasOpen) deactivate();
      wasOpen = openNow;
    }).observe(el, { attributes: true, attributeFilter: ['class', 'data-open', 'aria-hidden'] });
  }
  function init() {
    var cd = document.getElementById('cart-drawer');
    if (cd) watch(cd, function (el) { return el.classList.contains('is-open'); });
    document.querySelectorAll('[data-ikippah-drawer]').forEach(function (d) {
      watch(d, function (el) { return el.getAttribute('data-open') === 'true'; });
    });
    var so = document.querySelector('[data-js-search-overlay]');
    if (so) watch(so, function (el) { return el.getAttribute('data-open') === 'true'; });
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init, { once: true }); } else { init(); }
})();
